import React from 'react';
import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Select,
  Spinner,
  Tag,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useAppContext } from 'src/contexts/AppContext';
import { useModelDetailsQuery, ModelListItem } from 'src/api/models';
import { formatMediaLabel } from 'src/features/Settings/utils/formatters';
import { useCurrency } from 'src/hooks/useCurrency';
import { getCurrencyName } from 'src/utils/currency';
import { snakeToTitleCase } from 'src/utils/String';

type LtParentSelectProps = {
  eligibleParents: ModelListItem[];
  parentModelId?: string;
  onChange: (parentModelId: string) => void;
  isDisabled?: boolean;
};

/**
 * Parent-model picker for long-term models. The chosen Marketing Mix model
 * supplies the LT model's KPI, media, and campaign configuration (shown
 * read-only below the dropdown) as well as its priors at run time.
 */
export const LtParentSelect: React.FC<LtParentSelectProps> = ({
  eligibleParents,
  parentModelId,
  onChange,
  isDisabled = false,
}) => {
  const { currentCustomer } = useAppContext();
  const customerId = currentCustomer?.customerName ?? '';
  const { code: currencyCode } = useCurrency();

  const { data: parentDetails, isLoading: isLoadingParent } =
    useModelDetailsQuery(customerId, parentModelId ?? null);

  const selectedCampaignCount = parentDetails
    ? parentDetails.campaigns.filter((c) => !c.blacklist).length
    : 0;

  return (
    <Box
      border="1px solid"
      borderColor="surface.border"
      borderRadius="24px"
      p={6}
      bg="surface.panel">
      <FormControl>
        <FormLabel
          fontSize="16px"
          fontWeight={500}
          color="content.title"
          mb={2}>
          Based on model
        </FormLabel>
        <FormHelperText
          fontSize="14px"
          color="content.muted"
          mb={4}
          mt={0}
          lineHeight="1.5">
          The long-term model reuses the KPI, media, and campaign setup of the
          Marketing Mix model you pick, and builds its long-term estimates on
          top of that model&apos;s results.
        </FormHelperText>

        <Select
          placeholder="Select a Marketing Mix model"
          value={parentModelId ?? ''}
          onChange={(e) => onChange(e.target.value)}
          isDisabled={isDisabled}
          bg="surface.panel"
          border="1px solid"
          borderColor="surface.border"
          borderRadius="md"
          fontSize="16px"
          _focus={{
            borderColor: 'icon.green',
            boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
          }}>
          {eligibleParents.map((m) => (
            <option key={m.id} value={m.id}>
              {m.modelName}
            </option>
          ))}
        </Select>
      </FormControl>

      {parentModelId && (
        <Box mt={5}>
          {isLoadingParent || !parentDetails ? (
            <Spinner color="icon.green" size="sm" />
          ) : (
            <VStack align="stretch" spacing={3}>
              <HStack spacing={3}>
                <Text
                  fontSize="14px"
                  fontWeight={500}
                  color="content.body"
                  w="90px">
                  KPI
                </Text>
                <Text fontSize="14px" color="content.muted">
                  {parentDetails.kpiMetric
                    ? snakeToTitleCase(parentDetails.kpiMetric)
                    : '—'}
                  {parentDetails.kpiUnit
                    ? ` (${
                        parentDetails.kpiUnit === 'euro'
                          ? getCurrencyName(currencyCode)
                          : 'Unit'
                      })`
                    : ''}
                </Text>
              </HStack>
              <HStack spacing={3} align="flex-start">
                <Text
                  fontSize="14px"
                  fontWeight={500}
                  color="content.body"
                  w="90px">
                  Media
                </Text>
                <HStack spacing={2} flexWrap="wrap">
                  {parentDetails.mediaPlatforms.map((media) => (
                    <Tag key={media} size="sm" borderRadius="full">
                      {formatMediaLabel(media)}
                    </Tag>
                  ))}
                </HStack>
              </HStack>
              <HStack spacing={3}>
                <Text
                  fontSize="14px"
                  fontWeight={500}
                  color="content.body"
                  w="90px">
                  Campaigns
                </Text>
                <Text fontSize="14px" color="content.muted">
                  {selectedCampaignCount} selected
                </Text>
              </HStack>
            </VStack>
          )}
        </Box>
      )}
    </Box>
  );
};
